/**
 * Best-effort persistence for small Pinia snapshots (IndexedDB).
 * Not required for correctness: if data is cleared, lost, or writes fail, the app keeps working with defaults.
 */

const DB_NAME = 'gpx-spa-pinia-state';
const DB_VERSION = 1;
const STORE_NAME = 'stores';

type PersistedStoreRow = {
  id: string;
  payload: unknown;
  updatedAt: number;
};

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB.open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IDB transaction aborted'));
  });
}

export async function readPersistedStoreState<T>(
  storeId: string,
): Promise<T | null> {
  const db = await openDb();
  if (!db) {
    return null;
  }

  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const row = await new Promise<PersistedStoreRow | undefined>(
      (resolve, reject) => {
        const req = store.get(storeId);
        req.onerror = () => reject(req.error ?? new Error('IDB get failed'));
        req.onsuccess = () => resolve(req.result as PersistedStoreRow);
      },
    );
    await transactionDone(tx);
    return (row?.payload as T | undefined) ?? null;
  } finally {
    db.close();
  }
}

/**
 * IndexedDB structured clone cannot persist Vue/Pinia reactive proxies.
 * Round-trip through JSON to store only plain data.
 */
function toPlainClone(payload: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(payload)) as unknown;
  } catch {
    return null;
  }
}

export async function writePersistedStoreState(
  storeId: string,
  payload: unknown,
): Promise<void> {
  const plain = toPlainClone(payload);
  if (plain === null) {
    return;
  }

  const db = await openDb();
  if (!db) {
    return;
  }

  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({
      id: storeId,
      payload: plain,
      updatedAt: Date.now(),
    } as PersistedStoreRow);
    await transactionDone(tx);
  } finally {
    db.close();
  }
}
